import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Copy, Check, ArrowDownRight } from 'lucide-react';
import { AssistantMessageProps } from './types';
import { AgentResultSet, SearchResult } from '../../services/api';
import ToolCallDisplay from './ToolCallDisplay';
import StreamingText from './StreamingText';
import { getSearchResultKey, getSegmentIdentityKey } from '../../utils/searchResultIdentity';

const formatTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const RESULT_REFERENCE_PREFIX = 'result:';
const SEGMENT_REFERENCE_PREFIX = '/__segment_reference__/';

const findResultByHref = (
  href: string | undefined,
  resultMap: Map<string, SearchResult>,
  segmentMap: Map<string, SearchResult>,
): SearchResult | null => {
  if (!href) {
    return null;
  }

  if (href.startsWith(RESULT_REFERENCE_PREFIX)) {
    const encodedResultKey = href.slice(RESULT_REFERENCE_PREFIX.length);
    let decodedResultKey = encodedResultKey;
    try {
      decodedResultKey = decodeURIComponent(encodedResultKey);
    } catch {
      decodedResultKey = encodedResultKey;
    }
    return resultMap.get(decodedResultKey) || resultMap.get(encodedResultKey) || null;
  }

  if (href.startsWith('segment:')) {
    const segmentKey = decodeURIComponent(href.replace('segment:', ''));
    return segmentMap.get(segmentKey) || null;
  }

  if (href.startsWith(SEGMENT_REFERENCE_PREFIX)) {
    const segmentKey = decodeURIComponent(href.slice(SEGMENT_REFERENCE_PREFIX.length));
    return segmentMap.get(segmentKey) || null;
  }
  return null;
};

export const AssistantMessage: React.FC<AssistantMessageProps> = ({
  turnId,
  content,
  timestamp,
  toolCalls,
  resultSets,
  onSelectResultSet,
  onJumpToResults,
  onResultClick,
  isStreaming,
  streamingLabel,
  errorMessage,
}) => {
  const [copied, setCopied] = React.useState(false);

  const resolvedResultSets = useMemo<AgentResultSet[]>(() => resultSets || [], [resultSets]);
  const allResultSegments = useMemo(
    () => resolvedResultSets.flatMap((resultSet) => resultSet.results || []),
    [resolvedResultSets],
  );

  const resultMap = useMemo(() => {
    const map = new Map<string, SearchResult>();
    allResultSegments.forEach((result) => {
      const canonicalKey = getSearchResultKey(result);
      map.set(canonicalKey, result);
      if (typeof result.result_id === 'string' && result.result_id.trim().length > 0) {
        map.set(result.result_id, result);
      }
    });
    return map;
  }, [allResultSegments]);

  // Build segment lookup for click handling using full identity key
  const segmentMap = useMemo(() => {
    const map = new Map<string, SearchResult>();
    if (allResultSegments) {
      allResultSegments.forEach(seg => map.set(getSegmentIdentityKey(seg), seg));
    }
    return map;
  }, [allResultSegments]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleResultReferenceClick = (result: SearchResult) => {
    if (onResultClick) {
      onResultClick(result);
    }
  };

  // Custom link renderer for ReactMarkdown
  const linkRenderer = ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    const resolvedResult = findResultByHref(href, resultMap, segmentMap);
    if (resolvedResult) {
      return (
        <button
          type="button"
          className="segment-timestamp-link"
          onClick={() => handleResultReferenceClick(resolvedResult)}
          title="Click to view result"
        >
          {children}
        </button>
      );
    }
    // Never render navigational anchors in chat responses.
    return (
      <span className="segment-reference-text">
        {children}
      </span>
    );
  };

  const hasToolCalls = toolCalls && toolCalls.length > 0;
  const resultSetsWithResults = resolvedResultSets.filter((resultSet) => (resultSet.results || []).length > 0);

  return (
    <div className="agentic-message assistant-message">
      <div className="message-bubble assistant-bubble">
        {/* Tool calls section */}
        {hasToolCalls && (
          <div className="tool-calls-section">
            {toolCalls.map((tc) => (
              <ToolCallDisplay key={tc.id} toolCall={tc} />
            ))}
          </div>
        )}

        {/* Message content */}
        <div className="message-content">
          {isStreaming ? (
            <div className="streaming-message-content">
              {streamingLabel && (
                <div className="streaming-state-label">{streamingLabel}</div>
              )}
              <StreamingText content={content} />
            </div>
          ) : (
            <ReactMarkdown
              components={{
                a: linkRenderer,
              }}
            >
              {content}
            </ReactMarkdown>
          )}
        </div>

        {errorMessage && (
          <div className="assistant-message-error">{errorMessage}</div>
        )}

        {/* Copy button */}
        {!isStreaming && content && (
          <button
            className="copy-message-btn"
            onClick={handleCopy}
            title={copied ? 'Copied!' : 'Copy message'}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        )}
      </div>

      {resultSetsWithResults.length > 0 && (
        <div className="message-result-jumps" aria-label="Result sections for this message">
          {resultSetsWithResults.map((resultSet) => (
            <button
              key={resultSet.result_set_id}
              type="button"
              className="message-result-jump"
              onClick={() => {
                onSelectResultSet?.(turnId, resultSet.result_set_id);
                onJumpToResults?.(turnId, resultSet.result_set_id);
              }}
            >
              <ArrowDownRight size={13} />
              <span>{resultSet.label || 'Results'}</span>
              <strong>{resultSet.results.length}</strong>
            </button>
          ))}
        </div>
      )}

      {/* Timestamp */}
      {timestamp && !isStreaming && (
        <div className="message-timestamp assistant-timestamp">
          {formatTime(timestamp)}
        </div>
      )}
    </div>
  );
};

export default AssistantMessage;
